import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Button, Card, Divider } from '@mui/material';
import {
  FolderOpen as AllIcon,
  PeopleAltOutlined as PeopleIcon,
  LocalShippingOutlined as TruckIcon,
  BusinessOutlined as CompanyIcon,
  FolderOutlined as FolderIcon,
  FactCheckOutlined as BoardIcon,
  DeleteOutlineOutlined as TrashIcon,
  UploadFileOutlined as UploadIcon,
} from '@mui/icons-material';
import { childrenOf, useFileFolders } from './useFileFolders';
import { attentionUrgent, describeAttention } from './AttentionLine';
import { NavPanel, PanelGroup, PanelLink } from '../layout/NavPanel';
import PageBody from '../ui/PageBody';
import UploadDocumentDialog from './UploadDocumentDialog';
import DocumentService from '../../api/services/document.service';
import type { CategoryCount, DocumentOwnerType } from '../../api/services/document.service';

// The rail must look the same on every Files screen. Its folders come from
// useFileFolders, cached for the session. The "Needs attention" badge is
// cached the same way: only the home and board pages load the board, so on
// every other Files screen the count used to vanish.
let cachedAttention: number | null = null;

// Which rail item a Files URL belongs to. Matching each link against its
// own URL lit nothing on half the vault: a driver's file, a truck's file,
// a search and a document all live at URLs no link points to.
export type RailItem =
  | 'all' | 'attention' | 'drivers' | 'vehicles' | 'company' | 'deleted' | `category:${string}` | `folder:${string}`;

export function railSelection(pathname: string, currentCategory?: string): RailItem | null {
  const path = pathname.replace(/\/+$/, '');
  if (path === '/files' || path.startsWith('/files/search')) return 'all';
  if (path.startsWith('/files/compliance')) return 'attention';
  if (path.startsWith('/files/recently-deleted')) return 'deleted';
  // A person's or a truck's checklist, and the old roster pages, belong to
  // their folder.
  if (path.startsWith('/files/roster/driver') || path.startsWith('/files/entity/driver/')) return 'folder:people';
  if (path.startsWith('/files/roster/vehicle') || path.startsWith('/files/entity/vehicle/')) return 'folder:vehicles';
  // The Company checklist is the Company folder's own view.
  if (path.startsWith('/files/entity/company/')) return 'folder:company';
  // A folder lights its top-level folder — the rail lists only those.
  const folder = path.match(/^\/files\/folder\/([^/]+)/);
  if (folder) return `folder:${decodeURIComponent(folder[1]).split('.')[0]}`;
  const category = path.match(/^\/files\/category\/([^/]+)/);
  if (category) return `category:${decodeURIComponent(category[1])}`;
  // A single document belongs to its binder. Nothing is lit while it
  // loads, rather than lighting "All files" and then jumping.
  if (path.startsWith('/files/document/')) return currentCategory ? `category:${currentCategory}` : null;
  return null;
}

// The shell every Files screen sits in: the browser in the middle, an
// inspector on the right, and the vault rail — which now IS the app's
// contextual sidebar panel rather than a second sidebar standing next to
// the first. Files used to spend 472px on chrome (a 240px app sidebar plus
// this 232px rail); it spends 300px now, the same as every other screen,
// and the rail's contents are unchanged.
//
// The rail is still the thing that makes this read as a files app rather
// than a dashboard. Where you are is always visible, and the places you go
// most — people, trucks, the company drawer — are one click from anywhere
// instead of buried under a card at the bottom of a scroll.

export const INSPECTOR_WIDTH = 300;

// Opening the upload dialog from anywhere inside the shell — a page header,
// a missing row in an entity file, "Upload new version" on a document. The
// only way in used to be a button in the contextual panel, which a phone
// keeps behind the menu and a desk user simply did not look at; a missing
// requirement with nothing to click next to it read as "upload is missing".
export interface UploadPreset {
  // Preselects the document type, e.g. the row you clicked "Upload" on.
  typeKey?: string;
  // Locks the owner. Overrides the page-level uploadOwner.
  owner?: { ownerType: DocumentOwnerType; ownerId: string | null; label: string };
}

const UploadContext = createContext<(preset?: UploadPreset) => void>(() => undefined);

export const useFileUpload = () => useContext(UploadContext);

/** The page-header entry point. Same action as the panel button. */
export const FileDocumentButton: React.FC = () => {
  const openUpload = useFileUpload();
  return (
    <Button variant="contained" startIcon={<UploadIcon />} onClick={() => openUpload()}>
      File a document
    </Button>
  );
};

interface Props {
  /** No longer drawn — the rail lists folders. Accepted so callers need not change. */
  categories?: CategoryCount[];
  attentionCount?: number;
  // The open document's category, so its binder is lit in the rail.
  currentCategory?: string;
  // Called after a successful upload so the page can reload itself. The
  // dialog lives here: filing a document is the vault's primary action and
  // it should be one click from every screen in it, not only from the two
  // that happened to wire up a dialog of their own. Pages open it through
  // useFileUpload() / <FileDocumentButton />.
  onUploaded?: (documentId: string) => void;
  uploadOwner?: { ownerType: DocumentOwnerType; ownerId: string | null; label: string };
  inspector?: React.ReactNode;
  // The screen's <PageHeader>, passed separately so it spans the whole
  // content column rather than only the browser half. Its rule is the same
  // rule every other tab draws under its title, and it looked like a
  // different component when it stopped short of the inspector.
  header?: React.ReactNode;
  // A reading screen — the document viewer — rather than a browsing one.
  // It drops the shared max width and gives its children the full height of
  // the window. This is the one deliberate exception to "every screen is
  // capped at the same width": you are looking at one page of paper, and a
  // 1280px cap with a 640px-tall frame inside it made a document you had to
  // squint at. Browsing screens stay capped.
  reader?: boolean;
  children: React.ReactNode;
}

const FilesShell: React.FC<Props> = ({
  attentionCount, currentCategory, onUploaded, uploadOwner, inspector, header, reader, children,
}) => {
  const { pathname } = useLocation();
  const selected = railSelection(pathname, currentCategory);

  const [attention, setAttention] = useState<number>(attentionCount ?? cachedAttention ?? 0);
  useEffect(() => {
    if (attentionCount !== undefined) {
      cachedAttention = attentionCount;
      setAttention(attentionCount);
      return undefined;
    }
    if (cachedAttention !== null) return undefined;
    let active = true;
    DocumentService.getBoard()
      .then((board) => {
        cachedAttention = board.summary.needsAttention;
        if (active) setAttention(cachedAttention);
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, [attentionCount]);
  // null = closed. An object (possibly empty) = open with that preset.
  const [upload, setUpload] = useState<UploadPreset | null>(null);
  const openUpload = useCallback((preset?: UploadPreset) => setUpload(preset ?? {}), []);

  // The tree comes from the server, already in order; the rail shows its
  // top level.
  const { folders, reload: reloadFolders } = useFileFolders();
  const topFolders = childrenOf(folders, null);

  return (
    <UploadContext.Provider value={openUpload}>
      {/* ---------------------------------------------------------- rail */}
      {/* No upload button here any more — it is in each page's header
          (FileDocumentButton), where it is visible on a phone too. */}
      <NavPanel>
        <PanelGroup label="Vault">
          <PanelLink to="/files" icon={<AllIcon />} label="All files" active={selected === 'all'} currentPath={pathname} />
          <PanelLink
            to="/files/compliance"
            icon={<BoardIcon />}
            label="Needs attention"
            badge={attention}
            active={selected === 'attention'}
            currentPath={pathname}
          />
        </PanelGroup>

        {/* Drivers & staff and Vehicles are folders now, in the list below
            with everything else. */}

        {topFolders.length > 0 && (
          <PanelGroup label="Folders">
            {topFolders.map((folder) => (
              <PanelLink
                key={folder.key}
                to={`/files/folder/${folder.key}`}
                icon={folder.ownerType === 'driver' ? <PeopleIcon />
                  : folder.ownerType === 'vehicle' ? <TruckIcon />
                    : folder.key === 'company' ? <CompanyIcon /> : <FolderIcon />}
                label={folder.name}
                // One quiet mark, not two numbers: a dot when something in
                // here is missing or has run out. The counts are in the folder.
                dot={attentionUrgent(folder.attention) ? describeAttention(folder.attention) : null}
                active={selected === `folder:${folder.key}`}
                currentPath={pathname}
              />
            ))}
          </PanelGroup>
        )}

        <Divider sx={{ my: 1.5 }} />
        <PanelLink to="/files/recently-deleted" icon={<TrashIcon />} label="Recently deleted" active={selected === 'deleted'} currentPath={pathname} />
      </NavPanel>

      {/* ------------------------------------------------------- browser */}
      {/* One PageBody, capped and centred exactly like every other tab.
          The inspector used to be a third full-height chrome column outside
          the content column, which meant Files ignored the shared maximum
          width and hugged the left edge on a wide screen while every other
          tab sat centred. It is inside the column now — a sticky aside, not
          more chrome. */}
      <PageBody fluid={reader} fill={reader}>
        {header}
        <Box
          sx={{
            display: 'flex',
            alignItems: reader ? 'stretch' : 'flex-start',
            gap: 3,
            ...(reader && { flex: 1, minHeight: 0 }),
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, ...(reader && { display: 'flex', minHeight: 0 }) }}>
            {children}
          </Box>

          {inspector && (
            <Box
              component="aside"
              sx={{
                width: INSPECTOR_WIDTH,
                flexShrink: 0,
                display: { xs: 'none', lg: 'block' },
                ...(reader
                  ? { minHeight: 0, overflowY: 'auto' }
                  : { position: 'sticky', top: 0 }),
              }}
            >
              <Card sx={{ overflow: 'hidden' }}>{inspector}</Card>
            </Box>
          )}
        </Box>
      </PageBody>

      <UploadDocumentDialog
        open={upload !== null}
        onClose={() => setUpload(null)}
        onUploaded={(documentId) => { setUpload(null); reloadFolders(); onUploaded?.(documentId); }}
        fixedOwner={upload?.owner ?? uploadOwner}
        presetTypeKey={upload?.typeKey}
      />
    </UploadContext.Provider>
  );
};

export default FilesShell;
